import { useCallback, useEffect, useState } from 'react';
import {
	Button,
	DatePicker,
	Grid,
	Input,
	Modal,
	Select,
	Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { BatchFilter } from '../../../shared/VolunteerApplicationServiceTypes';
import { UndoOutlined } from '@ant-design/icons';
import { Status } from '../../../shared/ExamModuleTypes';
import { DATE_DD_MMM_YYYY } from '../../../shared/Constants';
import dayjs, { Dayjs, extend } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { isMoment } from 'moment';
import { formatDateToISOString } from '../../../shared/Utils';

extend(isSameOrBefore);

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface FilterProps {
	open: boolean;
	onClose: () => void;
	batchValue: BatchFilter;
	onBatchFilterChange: (value: BatchFilter) => void;
}

export const FilterBatch = ({
	open,
	onClose,
	batchValue,
	onBatchFilterChange,
}: FilterProps) => {
	const { t, i18n } = useTranslation();
	const isLengthyLanguage =
		i18n.language === 'ta' ||
		i18n.language === 'ma' ||
		i18n.language === 'te' ||
		i18n.language === 'ka';
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;

	// State
	const [batchName, setBatchName] = useState<string | undefined>();
	const [batchDescription, setBatchDescription] = useState<
		string | undefined
	>();
	const [startDate, setStartDate] = useState<Dayjs | null>(null);
	const [endDate, setEndDate] = useState<Dayjs | null>(null);
	const [status, setStatus] = useState<Status | undefined>();

	// Load from props
	useEffect(() => {
		setBatchName(batchValue.batchName);
		setBatchDescription(batchValue.batchDescription);
		// Convert Moment or string to Dayjs
		const start = batchValue.startDate
			? dayjs(
					isMoment(batchValue.startDate)
						? batchValue.startDate.toDate()
						: batchValue.startDate
			  )
			: null;

		const end = batchValue.endDate
			? dayjs(
					isMoment(batchValue.endDate)
						? batchValue.endDate.toDate()
						: batchValue.endDate
			  )
			: null;
		setStartDate(start);
		setEndDate(end);
		setStatus(batchValue.status);
	}, [batchValue]);

	// Reset filters
	const handleResetFilters = useCallback(() => {
		setBatchName(undefined);
		setBatchDescription(undefined);
		setStartDate(null);
		setEndDate(null);
		setStatus(undefined);
		onBatchFilterChange({});
		onClose();
	}, [onBatchFilterChange, onClose]);

	// Apply filters
	const handleFilterButtonClick = useCallback(() => {
		const defaultFilters: BatchFilter = {
			batchName,
			batchDescription,
			startDate: startDate
				? formatDateToISOString(startDate?.toDate(), 'start')
				: undefined,
			endDate: endDate
				? formatDateToISOString(endDate?.toDate(), 'end')
				: undefined,
			status,
		};
		onBatchFilterChange(defaultFilters);
		onClose();
	}, [
		onClose,
		onBatchFilterChange,
		batchDescription,
		batchName,
		startDate,
		endDate,
		status,
	]);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			centered
			className={
				isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
			}
			closable
			maskClosable
		>
			<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
				{t('filterBatch')}
			</div>

			<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
				{/* Batch Name */}
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('batchName')}
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={batchName}
							allowClear
							onChange={(e) => setBatchName(e.target.value)}
						/>
					</div>
				</div>

				{/* Batch Description */}
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('batchDescription')}
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={batchDescription}
							allowClear
							onChange={(e) => setBatchDescription(e.target.value)}
						/>
					</div>
				</div>

				{/* Start Date */}
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">{t('batchStartDate')}</Text>
						<DatePicker
							className="w-full"
							placeholder="Select Start Date"
							format={DATE_DD_MMM_YYYY}
							value={startDate}
							onChange={(date) => {
								setStartDate(date);
								// Reset end date if it becomes invalid
								if (endDate && date && endDate.isSameOrBefore(date, 'day')) {
									setEndDate(null);
								}
							}}
						/>
					</div>
				</div>

				{/* End Date */}
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">{t('batchEndDate')}</Text>
						<DatePicker
							className="w-full"
							placeholder="Select End Date"
							format={DATE_DD_MMM_YYYY}
							value={endDate}
							onChange={(date) => setEndDate(date)}
							disabledDate={(current) => {
								if (!current || !startDate) {
									return false;
								}
								// Disable startDate and any date before it
								return (
									current.isSame(startDate, 'day') ||
									current.isBefore(startDate, 'day')
								);
							}}
						/>
					</div>
				</div>

				{/* Status */}
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">{t('status')}</Text>
						<Select
							onChange={(value) => setStatus(value)}
							value={status}
							options={[
								{ value: 'ACTIVE', label: `${t('active')}` },
								{ value: 'INACTIVE', label: `${t('inactive')}` },
								{ value: 'IN_DRAFT', label: `${t('draft')}` },
							]}
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
						/>
					</div>
				</div>
			</div>

			<div className="flex flex-col items-center space-y-2 gap-3 md:flex-row md:justify-center md:gap-2 mt-4 ant-modal-footer">
				<Button
					className="w-full md:flex-1 h-9 text-primary-blue font-sans font-bold"
					type="link"
					onClick={handleResetFilters}
				>
					<span>
						{t('resetFilters')}
						<UndoOutlined className="ml-2" />
					</span>
				</Button>
				<Button
					className={`w-full md:flex-1 h-9 text-primary-blue font-sans font-bold ${
						isStackVertical ? 'p-2' : ''
					}`}
					onClick={onClose}
				>
					{t('closeFilter')}
				</Button>
				<Button
					type="primary"
					onClick={handleFilterButtonClick}
					className={`w-full md:flex-1 h-9 font-sans font-bold ${
						isStackVertical ? 'p-2 mt-3' : ''
					}`}
				>
					{t('applyFilter')}
				</Button>
			</div>
		</Modal>
	);
};
